import * as THREE from 'three';
import * as BufferGeometryUtils from 'three/examples/jsm/utils/BufferGeometryUtils.js';

// ─── Interfaces ────────────────────────────────────────────────────────────────

export interface BatchAnalysis {
  groups: BatchGroupInfo[];
  potentialDrawCallReduction: number;
  totalMeshes: number;
  totalVertices: number;
}

export interface BatchGroupInfo {
  materialKey: string;
  meshCount: number;
  totalVertices: number;
  totalIndices: number;
  estimatedBatches: number;
}

export interface BatchOptions {
  /** Max vertices per merged batch (default 65536 for 16-bit index compat) */
  maxVerticesPerBatch?: number;
  /** Min meshes to form a batch (default 3 - overhead not worth for 2) */
  minMeshCount?: number;
  /** Skip meshes marked as userData.dynamic (default true) */
  skipDynamic?: boolean;
}

export interface BatchResult {
  batchCount: number;
  meshesReduced: number;
  drawCallsSaved: number;
  batchIds: string[];
  timeMs: number;
}

interface StoredBatchData {
  id: string;
  materialKey: string;
  mergedMesh: THREE.Mesh;
  originalMeshes: THREE.Mesh[];
  originalVisibility: boolean[];
  originalParents: (THREE.Object3D | null)[];
}

// ─── Pre-allocated temp objects ────────────────────────────────────────────────

const _tempVec3 = new THREE.Vector3();
const _tempNormalMatrix = new THREE.Matrix3();

// ─── Material hashing ──────────────────────────────────────────────────────────

function hashMaterial(mat: THREE.Material): string {
  const m = mat as any;
  const parts: string[] = [m.type || 'unknown'];

  if (m.color) parts.push('c' + m.color.getHexString());
  if (m.emissive) parts.push('e' + m.emissive.getHexString());
  if (m.metalness !== undefined) parts.push('mt' + m.metalness.toFixed(2));
  if (m.roughness !== undefined) parts.push('rg' + m.roughness.toFixed(2));
  parts.push('t' + (mat.transparent ? '1' : '0'));
  parts.push('s' + mat.side);
  if (mat.opacity < 1) parts.push('o' + mat.opacity.toFixed(2));
  if (m.map) parts.push('map' + m.map.uuid.substring(0, 8));
  if (m.normalMap) parts.push('nm' + m.normalMap.uuid.substring(0, 8));
  if (mat.blending !== THREE.NormalBlending) parts.push('b' + mat.blending);
  if (mat.depthWrite === false) parts.push('dw0');

  return parts.join('_');
}

function isBatchable(mesh: THREE.Mesh, skipDynamic: boolean): boolean {
  // Skip non-standard mesh types
  if (mesh instanceof THREE.InstancedMesh) return false;
  if (mesh instanceof THREE.SkinnedMesh) return false;
  if ((mesh as any).isBatchedMesh) return false;
  if (mesh instanceof THREE.LineSegments || mesh instanceof THREE.Line) return false;
  if (mesh instanceof THREE.Points) return false;

  // Must have geometry and material
  if (!mesh.geometry) return false;
  if (!mesh.material) return false;
  if (Array.isArray(mesh.material)) return false; // multi-material not supported

  // Must have position attribute
  if (!mesh.geometry.attributes.position) return false;

  // Skip if morph targets are present (animated)
  if (mesh.geometry.morphAttributes && Object.keys(mesh.geometry.morphAttributes).length > 0) return false;
  if (mesh.morphTargetInfluences && mesh.morphTargetInfluences.length > 0) return false;

  // Skip dynamic meshes
  if (skipDynamic && mesh.userData?.dynamic) return false;
  if (skipDynamic && mesh.userData?.animated) return false;

  return true;
}

// ─── Main class ────────────────────────────────────────────────────────────────

export class FikoGeometryBatcher {
  private scene: THREE.Scene;
  private batches = new Map<string, StoredBatchData>();
  private meshToBatchId = new Map<THREE.Mesh, string>();
  private batchGroup: THREE.Group;
  private nextBatchId = 0;

  constructor(scene: THREE.Scene) {
    this.scene = scene;
    this.batchGroup = new THREE.Group();
    this.batchGroup.name = 'FikoGeometryBatches';
    this.batchGroup.frustumCulled = false;
    scene.add(this.batchGroup);
  }

  // ─── Analysis ──────────────────────────────────────────────────────────────

  /**
   * Analyze scene and find batchable groups (same material, static objects).
   * Does NOT modify the scene.
   */
  analyze(): BatchAnalysis {
    const materialGroups = new Map<string, THREE.Mesh[]>();
    let totalMeshes = 0;

    this.scene.traverse(obj => {
      if (!(obj instanceof THREE.Mesh)) return;
      if (!isBatchable(obj, true)) return;
      // Skip already-batched meshes
      if (this.meshToBatchId.has(obj)) return;
      // Skip our own batch group children
      if (this.isInBatchGroup(obj)) return;

      const mat = obj.material as THREE.Material;
      const key = hashMaterial(mat);

      if (!materialGroups.has(key)) materialGroups.set(key, []);
      materialGroups.get(key)!.push(obj);
      totalMeshes++;
    });

    const groups: BatchGroupInfo[] = [];
    let potentialReduction = 0;
    let totalVerts = 0;

    materialGroups.forEach((meshes, key) => {
      let verts = 0;
      let idxCount = 0;
      for (const m of meshes) {
        const posAttr = m.geometry.attributes.position;
        verts += posAttr.count;
        idxCount += m.geometry.index ? m.geometry.index.count : posAttr.count;
      }

      const maxVPB = 65536;
      const estimatedBatches = Math.max(1, Math.ceil(verts / maxVPB));

      groups.push({
        materialKey: key,
        meshCount: meshes.length,
        totalVertices: verts,
        totalIndices: idxCount,
        estimatedBatches
      });

      // Only count groups that would actually be batched (3+ meshes)
      if (meshes.length >= 3) {
        potentialReduction += meshes.length - estimatedBatches;
      }

      totalVerts += verts;
    });

    // Sort by potential impact (most meshes first)
    groups.sort((a, b) => b.meshCount - a.meshCount);

    return {
      groups,
      potentialDrawCallReduction: potentialReduction,
      totalMeshes,
      totalVertices: totalVerts
    };
  }

  // ─── Static Batching ──────────────────────────────────────────────────────

  /**
   * Batch a specific list of meshes that share materials.
   * Meshes are merged into one or more batched geometries (split by vertex limit).
   * Returns the newly created merged meshes.
   */
  batchStaticMeshes(meshes: THREE.Mesh[], options?: BatchOptions): THREE.Mesh[] {
    const maxVerts = options?.maxVerticesPerBatch ?? 65536;
    const minCount = options?.minMeshCount ?? 3;
    const skipDynamic = options?.skipDynamic ?? true;

    // Filter batchable
    const valid = meshes.filter(m => isBatchable(m, skipDynamic) && !this.meshToBatchId.has(m));
    if (valid.length < minCount) return [];

    // Group by material
    const matGroups = new Map<string, THREE.Mesh[]>();
    for (const m of valid) {
      const key = hashMaterial(m.material as THREE.Material);
      if (!matGroups.has(key)) matGroups.set(key, []);
      matGroups.get(key)!.push(m);
    }

    const result: THREE.Mesh[] = [];

    matGroups.forEach((groupMeshes, matKey) => {
      if (groupMeshes.length < minCount) return;

      // Split into sub-batches respecting vertex limit
      const subBatches = this.splitByVertexLimit(groupMeshes, maxVerts);

      for (const subBatch of subBatches) {
        if (subBatch.length < minCount) continue;
        const merged = this.mergeGroup(subBatch, matKey);
        if (merged) result.push(merged);
      }
    });

    return result;
  }

  // ─── Unbatch ───────────────────────────────────────────────────────────────

  /**
   * Restore original meshes from a batch, removing the merged mesh.
   */
  unbatch(batchId: string): THREE.Mesh[] {
    const batch = this.batches.get(batchId);
    if (!batch) return [];

    // Remove merged mesh
    this.batchGroup.remove(batch.mergedMesh);
    batch.mergedMesh.geometry.dispose();

    // Restore originals
    const restored: THREE.Mesh[] = [];
    for (let i = 0; i < batch.originalMeshes.length; i++) {
      const mesh = batch.originalMeshes[i];
      mesh.visible = batch.originalVisibility[i];

      // Re-add to original parent if it was removed
      const parent = batch.originalParents[i];
      if (parent && !mesh.parent) {
        parent.add(mesh);
      }

      this.meshToBatchId.delete(mesh);
      restored.push(mesh);
    }

    this.batches.delete(batchId);
    return restored;
  }

  /**
   * Unbatch all batches.
   */
  unbatchAll(): THREE.Mesh[] {
    const allRestored: THREE.Mesh[] = [];
    const ids = [...this.batches.keys()];
    for (const id of ids) {
      allRestored.push(...this.unbatch(id));
    }
    return allRestored;
  }

  // ─── Auto Batch ────────────────────────────────────────────────────────────

  /**
   * Analyze the entire scene and batch everything possible.
   */
  autoBatch(options?: BatchOptions): BatchResult {
    const startTime = performance.now();
    const opts: Required<BatchOptions> = {
      maxVerticesPerBatch: options?.maxVerticesPerBatch ?? 65536,
      minMeshCount: options?.minMeshCount ?? 3,
      skipDynamic: options?.skipDynamic ?? true
    };

    // Collect all batchable meshes
    const allMeshes: THREE.Mesh[] = [];
    this.scene.traverse(obj => {
      if (!(obj instanceof THREE.Mesh)) return;
      if (!isBatchable(obj, opts.skipDynamic)) return;
      if (this.meshToBatchId.has(obj)) return;
      if (this.isInBatchGroup(obj)) return;
      allMeshes.push(obj);
    });

    const created = this.batchStaticMeshes(allMeshes, opts);

    const elapsed = performance.now() - startTime;
    let meshesReduced = 0;
    let drawCallsSaved = 0;
    const batchIds: string[] = [];

    for (const [id, batch] of this.batches) {
      batchIds.push(id);
      meshesReduced += batch.originalMeshes.length;
      drawCallsSaved += batch.originalMeshes.length - 1; // N meshes → 1 draw call
    }

    if (created.length > 0) {
      console.log(
        `[FikoGeometryBatcher] autoBatch: ${meshesReduced} meshes → ${created.length} batches, ` +
        `${drawCallsSaved} draw calls saved in ${elapsed.toFixed(1)}ms`
      );
    }

    return {
      batchCount: created.length,
      meshesReduced,
      drawCallsSaved,
      batchIds,
      timeMs: elapsed
    };
  }

  // ─── Stats ─────────────────────────────────────────────────────────────────

  getStats(): { batchCount: number; meshesReduced: number; drawCallsSaved: number } {
    let meshesReduced = 0;
    let drawCallsSaved = 0;

    this.batches.forEach(batch => {
      meshesReduced += batch.originalMeshes.length;
      drawCallsSaved += batch.originalMeshes.length - 1;
    });

    return {
      batchCount: this.batches.size,
      meshesReduced,
      drawCallsSaved
    };
  }

  // ─── Dispose ───────────────────────────────────────────────────────────────

  dispose(): void {
    // Restore all originals
    this.unbatchAll();

    // Remove batch group from scene
    if (this.batchGroup.parent) {
      this.batchGroup.parent.remove(this.batchGroup);
    }

    this.batches.clear();
    this.meshToBatchId.clear();
  }

  // ─── Private helpers ───────────────────────────────────────────────────────

  /**
   * Split meshes into sub-arrays that each fit within the vertex limit.
   */
  private splitByVertexLimit(meshes: THREE.Mesh[], maxVerts: number): THREE.Mesh[][] {
    const result: THREE.Mesh[][] = [];
    let current: THREE.Mesh[] = [];
    let currentVerts = 0;

    for (const mesh of meshes) {
      const vertCount = mesh.geometry.attributes.position.count;

      // If a single mesh exceeds the limit, skip it (can't batch)
      if (vertCount > maxVerts) continue;

      if (currentVerts + vertCount > maxVerts && current.length > 0) {
        result.push(current);
        current = [];
        currentVerts = 0;
      }

      current.push(mesh);
      currentVerts += vertCount;
    }

    if (current.length > 0) {
      result.push(current);
    }

    return result;
  }

  /**
   * Merge a group of meshes into a single merged mesh.
   * Applies world transforms so the merged mesh sits at origin.
   */
  private mergeGroup(meshes: THREE.Mesh[], materialKey: string): THREE.Mesh | null {
    if (meshes.length === 0) return null;

    const batchId = `batch_${this.nextBatchId++}`;

    // Prepare geometries: clone each and apply world transform
    const preparedGeometries: THREE.BufferGeometry[] = [];

    for (const mesh of meshes) {
      mesh.updateMatrixWorld(true);
      const cloned = mesh.geometry.clone();

      // Apply world matrix to position
      const posAttr = cloned.attributes.position;
      if (posAttr) {
        for (let i = 0; i < posAttr.count; i++) {
          _tempVec3.set(posAttr.getX(i), posAttr.getY(i), posAttr.getZ(i));
          _tempVec3.applyMatrix4(mesh.matrixWorld);
          posAttr.setXYZ(i, _tempVec3.x, _tempVec3.y, _tempVec3.z);
        }
        posAttr.needsUpdate = true;
      }

      // Apply world rotation to normals
      const normAttr = cloned.attributes.normal;
      if (normAttr) {
        _tempNormalMatrix.getNormalMatrix(mesh.matrixWorld);
        for (let i = 0; i < normAttr.count; i++) {
          _tempVec3.set(normAttr.getX(i), normAttr.getY(i), normAttr.getZ(i));
          _tempVec3.applyMatrix3(_tempNormalMatrix).normalize();
          normAttr.setXYZ(i, _tempVec3.x, _tempVec3.y, _tempVec3.z);
        }
        normAttr.needsUpdate = true;
      }

      // Ensure consistent attributes: strip extras that not all geometries have
      // BufferGeometryUtils.mergeGeometries requires matching attributes
      preparedGeometries.push(cloned);
    }

    // Normalize attributes across all geometries before merging
    const normalizedGeometries = this.normalizeAttributes(preparedGeometries);
    if (normalizedGeometries.length === 0) return null;

    // Merge using Three.js utility
    let mergedGeo: THREE.BufferGeometry;
    try {
      mergedGeo = BufferGeometryUtils.mergeGeometries(normalizedGeometries, false);
    } catch (err) {
      // Fallback: manual merge if utility fails
      mergedGeo = this.manualMerge(normalizedGeometries);
    }

    // Dispose cloned geometries (we don't need them anymore)
    for (const g of normalizedGeometries) {
      g.dispose();
    }

    if (!mergedGeo) return null;

    mergedGeo.computeBoundingSphere();
    mergedGeo.computeBoundingBox();

    // Create merged mesh with original material
    const mergedMesh = new THREE.Mesh(mergedGeo, (meshes[0].material as THREE.Material).clone());
    mergedMesh.name = `GeoBatch_${materialKey.substring(0, 16)}_${batchId}`;
    mergedMesh.frustumCulled = true;
    mergedMesh.matrixAutoUpdate = false;

    // Store batch data
    const batchData: StoredBatchData = {
      id: batchId,
      materialKey,
      mergedMesh,
      originalMeshes: [...meshes],
      originalVisibility: meshes.map(m => m.visible),
      originalParents: meshes.map(m => m.parent)
    };

    this.batches.set(batchId, batchData);

    // Register meshes → batch mapping
    for (const m of meshes) {
      this.meshToBatchId.set(m, batchId);
      m.visible = false;
    }

    // Add merged mesh to batch group
    this.batchGroup.add(mergedMesh);

    return mergedMesh;
  }

  /**
   * Ensure all geometries have the same set of attributes so they can be merged.
   * Strips any attribute that doesn't exist in ALL geometries.
   */
  private normalizeAttributes(geometries: THREE.BufferGeometry[]): THREE.BufferGeometry[] {
    if (geometries.length === 0) return [];

    // Find common attributes
    const firstAttrs = new Set(Object.keys(geometries[0].attributes));
    const commonAttrs = new Set<string>();

    for (const attr of firstAttrs) {
      let allHave = true;
      for (let i = 1; i < geometries.length; i++) {
        if (!geometries[i].attributes[attr]) {
          allHave = false;
          break;
        }
      }
      if (allHave) commonAttrs.add(attr);
    }

    // Must at least have position
    if (!commonAttrs.has('position')) return [];

    // Strip non-common attributes
    for (const geo of geometries) {
      const toRemove: string[] = [];
      for (const attr of Object.keys(geo.attributes)) {
        if (!commonAttrs.has(attr)) {
          toRemove.push(attr);
        }
      }
      for (const attr of toRemove) {
        geo.deleteAttribute(attr);
      }
    }

    // Normalize index: ensure all indexed or all non-indexed
    const allIndexed = geometries.every(g => g.index !== null);
    const noneIndexed = geometries.every(g => g.index === null);

    if (!allIndexed && !noneIndexed) {
      // Convert non-indexed to indexed
      for (let i = 0; i < geometries.length; i++) {
        if (!geometries[i].index) {
          const count = geometries[i].attributes.position.count;
          const indices = new Uint32Array(count);
          for (let j = 0; j < count; j++) indices[j] = j;
          geometries[i].setIndex(new THREE.BufferAttribute(indices, 1));
        }
      }
    }

    return geometries;
  }

  /**
   * Fallback manual merge if BufferGeometryUtils fails.
   */
  private manualMerge(geometries: THREE.BufferGeometry[]): THREE.BufferGeometry {
    const positions: number[] = [];
    const normals: number[] = [];
    const uvs: number[] = [];
    const indices: number[] = [];
    let indexOffset = 0;

    for (const geo of geometries) {
      const posAttr = geo.attributes.position;

      for (let i = 0; i < posAttr.count; i++) {
        positions.push(posAttr.getX(i), posAttr.getY(i), posAttr.getZ(i));
      }

      const normAttr = geo.attributes.normal;
      if (normAttr) {
        for (let i = 0; i < normAttr.count; i++) {
          normals.push(normAttr.getX(i), normAttr.getY(i), normAttr.getZ(i));
        }
      }

      const uvAttr = geo.attributes.uv;
      if (uvAttr) {
        for (let i = 0; i < uvAttr.count; i++) {
          uvs.push(uvAttr.getX(i), uvAttr.getY(i));
        }
      }

      if (geo.index) {
        for (let i = 0; i < geo.index.count; i++) {
          indices.push(geo.index.getX(i) + indexOffset);
        }
      } else {
        for (let i = 0; i < posAttr.count; i++) {
          indices.push(i + indexOffset);
        }
      }

      indexOffset += posAttr.count;
    }

    const merged = new THREE.BufferGeometry();
    merged.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));

    if (normals.length > 0) {
      merged.setAttribute('normal', new THREE.Float32BufferAttribute(normals, 3));
    }

    if (uvs.length > 0) {
      merged.setAttribute('uv', new THREE.Float32BufferAttribute(uvs, 2));
    }

    if (indices.length > 0) {
      // Use Uint32 for large merged buffers, Uint16 if small enough
      if (indexOffset <= 65536) {
        merged.setIndex(new THREE.Uint16BufferAttribute(indices, 1));
      } else {
        merged.setIndex(new THREE.Uint32BufferAttribute(indices, 1));
      }
    }

    return merged;
  }

  /**
   * Check if an object is inside the batch group.
   */
  private isInBatchGroup(obj: THREE.Object3D): boolean {
    let current: THREE.Object3D | null = obj;
    while (current) {
      if (current === this.batchGroup) return true;
      current = current.parent;
    }
    return false;
  }
}
